import React from 'react';
import { NetworkStatus } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';

import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import LoadingSpinner, {
  SIZES
} from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import PostIndexCard from 'buyside/components/presentation/PostIndex/postIndexCard';
import PostIndexHeader from 'buyside/components/presentation/PostIndex/postIndexHeader';

import { usePostsIndexListQuery } from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/PostIndex/postIndexStyles.scss';

const PostIndexContainer: React.FC = (): React.ReactElement => {
  const [searchParams] = useSearchParams();

  const { data, error, fetchMore, networkStatus } = usePostsIndexListQuery({
    variables: { searchParams: searchParams.toString() },
    notifyOnNetworkStatusChange: true
  });

  if (networkStatus === NetworkStatus.loading || !data)
    return (
      <>
        <PostIndexHeader resultCount="--" loading />
        <LoadingSpinner size={SIZES.LARGE} />
      </>
    );

  if (error || !data.postsIndexList || !data.currentUser) throw error;

  const {
    postsIndexList: posts,
    currentUser: {
      id: currentUserId,
      acceptedPostTerms,
      canPost,
      canViewUserDetails,
      isAdmin,
      profileImageUrl
    }
  } = data;

  return (
    <>
      <PostIndexHeader
        canPost={Boolean(canPost)}
        isAdmin={Boolean(isAdmin)}
        profileImageUrl={profileImageUrl}
        resultCount={data.postsIndexListResultCount || 0}
        termsAccepted={Boolean(acceptedPostTerms)}
      />
      {posts.edges?.map(edge => {
        const isAuthor = currentUserId === edge?.node?.user?.id;
        return (
          edge?.node && (
            <PostIndexCard
              key={edge.node.id}
              {...edge.node}
              isAuthor={isAuthor}
              isIndex
              showUserDetails={Boolean(canViewUserDetails || isAuthor)}
            />
          )
        );
      })}
      {networkStatus === NetworkStatus.fetchMore && <LoadingSpinner size={SIZES.MEDIUM} />}
      {posts.pageInfo.hasNextPage && posts.pageInfo.endCursor && (
        <Button
          id="load-more-posts"
          className={styles.loadMoreButton}
          onClick={(): void => {
            fetchMore({ variables: { cursor: posts.pageInfo.endCursor } });
          }}
          text="Load More"
          type={BUTTON_TYPES.PRIMARY}
        />
      )}
    </>
  );
};

export default PostIndexContainer;
